import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, ButtonGroup, Col, Form, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import { BVCM_RESULT_REVIEW, getURL } from 'lib/networking/endpoints';
import { coolGrey } from 'lib/css/colors';
import LinkButton from 'components/shared/LinkButton';
import {
  packOf,
  productUrl,
  recordUrl,
  REVIEW_NOTE_MAX_LENGTH,
  VERDICTS,
} from 'components/bvcm_review/bvcmReview';
import { SwalToast } from 'lib/utils/toast';
import useDelete from 'lib/hooks/useDelete';
import usePut from 'lib/hooks/usePut';

function Fact({ label, children }) {
  return (
    <div className="mb-2">
      <small className={css(styles.label)}>{label}</small>
      <div>{children || '-'}</div>
    </div>
  );
}

Fact.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

function BvcmResultDetail({ runId, row, review, canReview, onReviewChange }) {
  const productId = row.fsa_vendor_product_id;
  const current = review?.verdict;
  const [note, setNote] = useState(review?.review_note || '');
  useEffect(() => setNote(review?.review_note || ''), [review]);

  const reviewUrl = getURL(BVCM_RESULT_REVIEW, { run_id: runId, product_id: productId });
  const onFailure = useCallback(() => SwalToast('error', 'Could not save the review'), []);
  const { putData, loading: saving } = usePut(reviewUrl, onReviewChange, onFailure);
  const { deleteData, loading: clearing } = useDelete(reviewUrl, false, onReviewChange, onFailure);

  const links = [
    {
      text: 'Distributor product',
      url: productUrl(row.fsa_global_product_id, productId, 'distributor'),
    },
    {
      text: 'Matched product',
      url: productUrl(
        row.fsa_matched_product_global_id,
        row.fsa_matched_product_id,
        'manufacturer',
      ),
    },
    { text: 'Brand', url: recordUrl('brand-details', row.fsa_brand_id) },
    { text: 'Manufacturer', url: recordUrl('manufacturer-details', row.fsa_manufacturer_id) },
  ].filter(link => link.url);

  return (
    <div className={css(styles.detail)}>
      <div className="mb-3">
        {links.map(link => (
          <span key={link.text} className="mr-3">
            <LinkButton text={link.text} url={link.url} />
          </span>
        ))}
      </div>
      <Row>
        <Col md={4}>
          <Fact label="Distributor sends">
            {row.distributor_product_name}
            <br />
            <small className={css(styles.label)}>
              {[
                `Code ${row.distributor_mfr_product_code || '-'}`,
                `Pack ${packOf(row.distributor_pack_size)}`,
                `Brand ${row.distributor_brand_name || '-'}`,
                `Maker ${row.distributor_mfr_name || '-'}`,
              ].join(' · ')}
            </small>
          </Fact>
          <Fact label="Manufacturer in FSA">
            {row.fsa_mfr_from_brand} {row.mfr_evidence_strength && `(${row.mfr_evidence_strength})`}
          </Fact>
          <Fact label="Why">{row.reason}</Fact>
        </Col>
        <Col md={4}>
          <Fact label="Target">
            {row.action === 'CREATE'
              ? `New product under the next UPB placeholder SKU, pack ${packOf(
                  row.distributor_pack_size,
                )}`
              : row.fsa_matched_product &&
                `${row.fsa_matched_product} (SKU ${
                  row.fsa_matched_product_sku || '-'
                } · Pack ${packOf(row.matched_product_pack_size)})`}
          </Fact>
          <Fact label="Pick">
            {row.name_pick_confidence} {row.name_pick_reason}
          </Fact>
          <Fact label="Size check">
            {row.size_check_relation || row.name_size_relation}{' '}
            {row.size_check_confidence && `(${row.size_check_confidence})`} {row.size_check_reason}
          </Fact>
        </Col>
        <Col md={4}>
          <Fact label={`Audit ${row.audit_check}`}>
            {row.audit_verdict} {row.audit_confidence && `(${row.audit_confidence})`}{' '}
            {row.audit_reason}
          </Fact>
          <Fact label="Apply">
            {row.apply_status} {row.apply_reason_code} {row.apply_detail}
          </Fact>
          {canReview ? (
            <>
              <Form.Group controlId={`bvcm-note-${productId}`} className="mb-2">
                <Form.Label className={css(styles.label)}>
                  <small>Review note (saved with the verdict)</small>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={REVIEW_NOTE_MAX_LENGTH}
                  placeholder="Why, especially for Wrong or Unsure"
                  value={note}
                  onChange={event => setNote(event.target.value)}
                />
              </Form.Group>
              <ButtonGroup aria-label="Review verdict">
                {VERDICTS.map(verdict => (
                  <Button
                    key={verdict.key}
                    size="sm"
                    variant={
                      verdict.key === current ? verdict.variant : `outline-${verdict.variant}`
                    }
                    aria-pressed={verdict.key === current}
                    disabled={saving || clearing}
                    onClick={() => putData({ verdict: verdict.key, review_note: note })}
                  >
                    {verdict.label}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={saving || clearing}
                  onClick={() => deleteData({})}
                >
                  Clear
                </Button>
              </ButtonGroup>
            </>
          ) : (
            review?.review_note && <Fact label="Review note">{review.review_note}</Fact>
          )}
        </Col>
      </Row>
    </div>
  );
}

const styles = StyleSheet.create({
  detail: {
    padding: 16,
  },
  label: {
    color: coolGrey,
  },
});

BvcmResultDetail.propTypes = {
  runId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  row: PropTypes.object.isRequired,
  review: PropTypes.shape({ verdict: PropTypes.string, review_note: PropTypes.string }),
  canReview: PropTypes.bool,
  onReviewChange: PropTypes.func.isRequired,
};

export default BvcmResultDetail;
